import { useContext, useState } from 'react';
import { useMutation } from '@apollo/client/react';

import DeleteModal from '@atom/components/common/DeleteModal';
import { USER_UPDATE } from '@atom/graph/user';
import { usePreferences } from '@atom/hooks/usePreferences';
import { Icon, IconButton, Menu, Snackbar } from '@atom/mui';
import { DeleteIcon, MoreVertIcon } from '@atom/mui/icons';
import { UserUserGroup } from '@atom/types/userGroups';
import { hasRolePermissions, ROLE_SETS } from '@atom/utilities/authUtilities';
import { getUserFullName } from '@atom/utilities/userUtilities';
import { getErrorCode } from '@atom/utilities/validationUtilities';

import UserContext from '../UserContext';

const { MenuItem } = Menu;

const CONTROL_CELL_SIZE = '48px';

interface Props {
  group: UserUserGroup;
  handleRefetch: () => void;
}

const UserGroupsControlCell = ({ group, handleRefetch }: Props) => {
  const { user } = useContext(UserContext);
  const [openDelete, setOpenDelete] = useState<boolean>(false);

  const preferences = usePreferences();

  const [updateUser] = useMutation(USER_UPDATE, {
    onCompleted: () => {
      handleRefetch();
    },
  });

  const handleDelete = async (groupId: string) => {
    try {
      const userGroups: string[] = user.userGroups.reduce(
        (groupIds, nextGroup) => {
          return nextGroup.id === groupId
            ? groupIds
            : [...groupIds, nextGroup.id];
        },
        [],
      );
      await updateUser({
        variables: {
          input: {
            id: user.id,
            userGroups,
          },
        },
      });

      Snackbar.info({ message: 'Deleted Group' });
      setOpenDelete(false);
    } catch (error) {
      if (getErrorCode(error) === 422) {
        Snackbar.error({
          message:
            'The user has unapproved time entries for the group you wish to remove',
        });
      }

      setOpenDelete(false);
    }
  };

  const handleSetDefault = async (groupId: string) => {
    try {
      await updateUser({
        variables: {
          input: {
            id: user.id,
            defaultUserGroupId: groupId,
          },
        },
      });

      const getSetDefaultMessage = () => {
        const pathTo = group.groupPath.slice(0, -1);
        const messageParts = [
          '"',
          ...(pathTo.length
            ? `${group.groupPath.slice(0, -1).join(' / ')} -`
            : ''),
          `${group.name}" set as default group.`,
        ];
        return messageParts.join('');
      };

      const message =
        groupId === '' ? 'Default group cleared' : getSetDefaultMessage();

      Snackbar.info({ message });
    } catch (error) {
      Snackbar.error({
        message: 'An error occurred, please try again',
      });
    }
  };

  const canSetDefaultGroup =
    preferences?.timeTracking?.timesheet &&
    hasRolePermissions(ROLE_SETS.ORG_ADMIN);

  const contents = canSetDefaultGroup ? (
    <Menu icon={<Icon icon={MoreVertIcon} />}>
      {user.defaultUserGroup?.id === group.id ? (
        <MenuItem onClick={() => handleSetDefault('')}>Clear Default</MenuItem>
      ) : (
        <MenuItem onClick={() => handleSetDefault(group.id)}>
          Set as Default
        </MenuItem>
      )}

      <MenuItem onClick={() => setOpenDelete(true)}>Remove</MenuItem>
    </Menu>
  ) : hasRolePermissions(ROLE_SETS.ORG_ADMIN) ? (
    <>
      <IconButton onClick={() => setOpenDelete(true)}>
        <Icon icon={DeleteIcon} />
      </IconButton>
    </>
  ) : (
    <div style={{ height: CONTROL_CELL_SIZE }} />
  );

  return (
    <>
      {contents}
      <DeleteModal
        title="Remove User from Group"
        open={openDelete}
        onCancel={() => setOpenDelete(false)}
        onConfirm={() => handleDelete(group.id)}
        confirmText="Remove"
        content={`Are you sure you want to remove ${getUserFullName(user)} from ${group.name}?`}
      />
    </>
  );
};

export default UserGroupsControlCell;
